import type { AiChatMessage, AiChatMessageMeta } from "@/types/ai-chat";
import { type Dispatch, type SetStateAction, useCallback } from "react";

export type AiChatRefreshPayload = {
  tabs?: number[];
  profiles?: boolean;
  time_state?: {
    profile_id?: number;
  };
};

export type AiChatStreamResult = {
  text: string;
  refreshPayload: AiChatRefreshPayload | null;
  messageMeta: AiChatMessageMeta | null;
};

export interface UseAiChatStreamParams {
  setMessages: Dispatch<SetStateAction<AiChatMessage[]>>;
  applyMessageMeta: (assistantId: string, messageMeta: AiChatMessageMeta, userId?: string) => void;
}

export function useAiChatStream({ setMessages, applyMessageMeta }: UseAiChatStreamParams) {
  return {
    streamAiChatResponse: useCallback(
      async (rawResponse: Response, assistantId: string, userId?: string): Promise<AiChatStreamResult> => {
        const body = rawResponse.body;
        if (!body) {
          throw new Error("Empty response body");
        }

        const reader = body.getReader();
        const decoder = new TextDecoder();
        const metaPrefix = "[AI_META]";
        let metaBuffer = "";
        let metaDetected = false;
        let refreshPayload: AiChatRefreshPayload | null = null;
        let messageMeta: AiChatMessageMeta | null = null;
        let accumulatedText = "";

        const appendChunk = (chunk: string) => {
          if (!chunk) {
            return;
          }
          accumulatedText += chunk;
          setMessages((prev) => {
            const next = [...prev];
            const idx = next.findIndex((m) => m.id === assistantId);
            if (idx !== -1) {
              next[idx] = { ...next[idx], content: next[idx].content + chunk };
            }
            return next;
          });
        };

        while (true) {
          const { value, done } = await reader.read();
          if (done || !value) {
            break;
          }
          const chunk = decoder.decode(value, { stream: true });
          const metaIndex = chunk.indexOf(metaPrefix);
          if (metaIndex >= 0) {
            const visible = chunk.slice(0, metaIndex);
            if (visible) {
              appendChunk(visible);
            }
            metaBuffer += chunk.slice(metaIndex + metaPrefix.length);
            metaDetected = true;
            continue;
          }
          if (metaDetected) {
            metaBuffer += chunk;
            continue;
          }
          appendChunk(chunk);
        }

        if (metaBuffer.trim()) {
          const parsed = JSON.parse(metaBuffer.trim());
          refreshPayload = parsed?.refresh ?? null;
          if (parsed?.message_meta && typeof parsed.message_meta === "object") {
            messageMeta = parsed.message_meta as AiChatMessageMeta;
          }
        }

        if (messageMeta) {
          applyMessageMeta(assistantId, messageMeta, userId);
        }

        return { text: accumulatedText, refreshPayload, messageMeta };
      },
      [applyMessageMeta, setMessages],
    ),
  };
}
